/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/device/replayer.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)    | Who               | Why                    | What Changed         |
 * |---|----------------------|-------------------|------------------------|----------------------|
 * | 1 | 2026-06-16 (Step 7)  | Claude Sonnet 4.6 | Replay v0 (Step 7)     | Created from scratch |
 * | 2 | 2026-06-16 (cmds)    | Claude Sonnet 4.6 | Custom commands        | Handle launchApp, stopApp, pressKey, wait, screenshot, typeText; add "error" status + detail field |
 * | 3 | 2026-06-16 (edit)    | Claude Sonnet 4.6 | Step editing / reliability | Add matchedBy to ReplayStepResult; use fallbackCoord as last resort for tap steps |
 * | 4 | 2026-06-17 (live-progress) | Claude Sonnet 4.6 | Live replay progress | Add onStepStart + onStepResult callbacks to ReplayerDeps; call them in loop so UI can show live step status |
 * | 5 | 2026-06-17 (heal-hint)    | Claude Sonnet 4.6 | Phase 2 heal bridge  | Add healHint to ReplayStepResult: when not_found AND selector has fingerprint, surface CLI --heal tip |
 * | 6 | 2026-06-18 (exec-ctrl)    | Claude Sonnet 4.6 | Phase 3b Execution tab | Add ExecutionController (pause/stop) to ReplayerDeps; loop polls between steps |
 * | 7 | 2026-06-18 (commands)     | Claude Sonnet 4.6 | Phase 3 command expansion | Add 10 new handlers: doubleTap, longPress, swipe, clearText, hideKeyboard, waitFor, openUrl, deepLink, setDeviceState, scrollUntilVisible |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx                     (replaySteps, ReplayStepResult)
 * - apps/studio/src/components/ExecutionTab.tsx (replaySteps, ExecutionController)
 * - apps/studio/src/device/replayer.test.ts     (unit tests)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - ExecutionController — { paused, stopped } mutable flags polled between steps
 * - makeController()    — factory for ExecutionController
 * - ReplayStepResult    — per-step outcome: ok | not_found | error
 * - ReplayerDeps        — injectable dumpTree + injectTap + callbacks + controller
 * - replaySteps(steps, adb, deps?) → Promise<ReplayStepResult[]>
 *
 * STEP TYPE COVERAGE
 * ------------------
 * Implemented: tap, doubleTap, longPress, typeText, clearText, pressKey,
 *   hideKeyboard, swipe, scrollUntilVisible, waitFor, wait, screenshot,
 *   launchApp, stopApp, openUrl, deepLink, setDeviceState
 * Not implemented (control flow — Phase 3.5): if, repeat, forEachRow, callFlow, api, script
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. ExecutionController is polled BEFORE each step.
 * 2. clearText: focuses element, moves to end, sends 200 backspaces. Covers
 *    most form fields. Long text fields may need multiple clearText steps.
 * 3. scrollUntilVisible: swipes in the given direction, polls the tree after
 *    each swipe. Returns not_found if maxScrolls exhausted without match.
 * 4. waitFor state="hidden": succeeds when selector returns null (element gone).
 * 5. openUrl / deepLink use `am start ACTION_VIEW`. Deep link adds -p <pkg> if
 *    packageName provided (constrains routing on Android 12+).
 *
 * =============================================================================
 */

import type { Adb } from "@yume-chan/adb";
import type { IRStep, PressKeyName } from "@mimiq/ir";
import type { A11yNode } from "./treeParser.js";
import { resolveTarget } from "./resolver.js";

/* ── Control-flow signals ────────────────────────────────────────────────── */

export class BreakLoopSignal extends Error {
  constructor() { super("BREAK"); this.name = "BreakLoopSignal"; }
}
export class ContinueLoopSignal extends Error {
  constructor() { super("CONTINUE"); this.name = "ContinueLoopSignal"; }
}
export class StopTestSignal extends Error {
  constructor(public readonly verdict: "passed" | "failed", msg: string) {
    super(msg); this.name = "StopTestSignal";
  }
}

/* ── ExecutionController ─────────────────────────────────────────────────── */

export interface ExecutionController {
  paused: boolean;
  stopped: boolean;
}

export function makeController(): ExecutionController {
  return { paused: false, stopped: false };
}

/* ── Variable interpolation ──────────────────────────────────────────────────
 *
 * Syntax: ${variableName}  →  value from context (or left as-is if missing)
 *         \${...}          →  literal "${...}" (escape)
 *
 * Supported prefixes in value keys:
 *   env:KEY   →  context["env:KEY"]   (set from IR config env block)
 *   bare name →  context["name"]      (forEachRow row variable, script output)
 */
// ── String-function regex: ${fnName(arg1,arg2,...)} ─────────────────────────
const INTERP_FUNC_RE = /\$\{(\w+)\(([^)]*)\)\}/g;
const INTERP_VAR_RE  = /\\(\$\{[^}]*\})|(\$\{([^}]+)\})/g;

export function interpolate(template: string, context: Record<string, unknown> = {}): string {
  // Pass 1 — built-in string functions: ${trim(x)}, ${upper(x)}, ${lower(x)}, etc.
  const pass1 = template.replace(INTERP_FUNC_RE, (_m, fn: string, rawArgs: string) => {
    const args = rawArgs.split(",").map((a) => {
      const t = a.trim();
      // Quoted literal: "hello" or 'hello'
      if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) return t.slice(1, -1);
      // Variable reference
      return context[t] !== undefined ? String(context[t]) : t;
    });
    const [a = "", b = "", c = ""] = args;
    switch (fn.toLowerCase()) {
      case "trim":    return a.trim();
      case "upper":   return a.toUpperCase();
      case "lower":   return a.toLowerCase();
      case "length":  return String(a.length);
      case "substr":  return a.substring(parseInt(b) || 0, c !== "" ? parseInt(c) : undefined);
      case "replace": return a.replaceAll(b, c);
      case "concat":  return args.join("");
      case "number":  return String(parseFloat(a) || 0);
      case "abs":     return String(Math.abs(parseFloat(a) || 0));
      case "round":   return String(Math.round(parseFloat(a) || 0));
      default:        return _m; // unknown function — leave unchanged
    }
  });
  // Pass 2 — plain variable substitution: ${varName} and escaped \${...}
  return pass1.replace(INTERP_VAR_RE, (_match, escaped, _full, key) => {
    if (escaped !== undefined) return escaped;
    const val = context[key as string];
    return val !== undefined ? String(val) : `\${${key as string}}`;
  });
}

/* ── Types ───────────────────────────────────────────────────────────────────*/

export interface ReplayStepResult {
  stepIndex: number;
  stepId: string;
  status: "ok" | "not_found" | "error";
  x?: number;
  y?: number;
  detail?: string;
  matchedBy?: string;
  healHint?: string;
  screenshotRef?: string;
  a11yDump?: string;   // JSON-stringified A11yNode[] captured on failure
}

export interface ReplayerDeps {
  dumpTree?: (adb: Adb) => Promise<A11yNode[]>;
  injectTap?: (adb: Adb, x: number, y: number) => Promise<void>;
  stepDelayMs?: number;
  onStepStart?: (stepIndex: number) => void;
  onStepResult?: (result: ReplayStepResult) => void;
  controller?: ExecutionController;
  /** Variables available for ${var} interpolation in print steps (and future script steps). */
  runContext?: Record<string, unknown>;
  /** When set, screenshot steps pull PNG to OPFS at runs/{runId}/step_{i}.png */
  runId?: string;
  /** When true, capture screenshot after EVERY step (configurable in Settings). */
  captureEveryStep?: boolean;
  /** When true, dump the a11y tree on any failed step for debug logs. */
  captureLogsOnFailure?: boolean;
  /** Loader for shared function library steps (provides Studio DB access). */
  sharedFlowLoader?: (id: string) => Promise<{ name: string; steps: import("@mimiq/ir").IRStep[]; parameters: { name: string; defaultValue?: string }[] } | undefined>;
  /**
   * Optional sandbox runner for script steps. When absent, script steps
   * return "error: no sandbox available" (safe default — never eval()).
   * Studio provides an iframe postMessage runner; CLI provides node:vm.
   */
  scriptRunner?: (code: string, vars: Record<string, unknown>) => Promise<ScriptResult>;
}

/** Return value from a sandboxed script step. */
export interface ScriptResult {
  /** Variables to merge into the run context. */
  vars?: Record<string, unknown>;
  /** If set, the step fails with this message. */
  fail?: string;
}

/* ── ADB keycode map ─────────────────────────────────────────────────────── */

const KEYCODE_MAP: Record<PressKeyName, string> = {
  back: "KEYCODE_BACK",
  home: "KEYCODE_HOME",
  enter: "KEYCODE_ENTER",
  tab: "KEYCODE_TAB",
  delete: "KEYCODE_DEL",
};

/* ── Defaults ─────────────────────────────────────────────────────────────── */

const DEFAULT_STEP_DELAY_MS = 800;

async function defaultInjectTap(adb: Adb, x: number, y: number): Promise<void> {
  await adb.subprocess.noneProtocol.spawnWaitText([
    "input", "tap", x.toString(), y.toString(),
  ]);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitWhilePaused(ctrl: ExecutionController): Promise<boolean> {
  while (ctrl.paused && !ctrl.stopped) await sleep(50);
  return ctrl.stopped;
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

/** Resolve a selector, optionally using fallbackCoord. Returns null if nothing found. */
function resolveWithFallback(
  step: Extract<IRStep, { selector: unknown }> & { fallbackCoord?: [number, number] },
  tree: A11yNode[],
): { x: number; y: number; matchedBy: string } | null {
  const target = resolveTarget((step as { selector: Parameters<typeof resolveTarget>[0] }).selector, tree);
  if (target) return target;
  if ("fallbackCoord" in step && step.fallbackCoord) {
    return { x: step.fallbackCoord[0], y: step.fallbackCoord[1], matchedBy: "coord" };
  }
  return null;
}

/** Flatten the A11y tree into a single array for attribute lookups. */
function flatNodes(nodes: A11yNode[]): A11yNode[] {
  const out: A11yNode[] = [];
  function walk(n: A11yNode) { out.push(n); n.children?.forEach(walk); }
  nodes.forEach(walk);
  return out;
}

/**
 * Check if a selector + assertion condition is satisfied against the current tree.
 * Returns { met: boolean, detail: string }.
 */
/** Evaluate a value-comparison expression. Returns { met, detail }. */
function evaluateComparison(
  left: string,
  operator: string,
  right: string | undefined,
  options?: { trim?: boolean; ignoreCase?: boolean; parseNumber?: boolean },
): { met: boolean; detail: string } {
  const L = options?.trim ? left.trim()  : left;
  const R = options?.trim ? (right ?? "").trim() : (right ?? "");
  const l = options?.ignoreCase ? L.toLowerCase() : L;
  const r = options?.ignoreCase ? R.toLowerCase() : R;

  let met: boolean;
  switch (operator) {
    case "==":           met = l === r; break;
    case "!=":           met = l !== r; break;
    case "contains":     met = l.includes(r); break;
    case "notContains":  met = !l.includes(r); break;
    case "startsWith":   met = l.startsWith(r); break;
    case "endsWith":     met = l.endsWith(r); break;
    case "matches":      try { met = new RegExp(R).test(L); } catch { met = false; } break;
    case "isEmpty":      met = L.trim() === ""; break;
    case "isNotEmpty":   met = L.trim() !== ""; break;
    case ">": case "<": case ">=": case "<=": {
      const lN = parseFloat(L), rN = parseFloat(R);
      if (isNaN(lN) || isNaN(rN)) { met = false; break; }
      if (operator === ">")  { met = lN > rN;  break; }
      if (operator === "<")  { met = lN < rN;  break; }
      if (operator === ">=") { met = lN >= rN; break; }
      met = lN <= rN; break;
    }
    default: met = false;
  }
  const detail = `"${L}" ${operator} "${R}" → ${met ? "✓ true" : "✗ false"}`;
  return { met, detail };
}

function checkCondition(
  selector: Parameters<typeof resolveTarget>[0],
  assertion: "visible" | "notVisible" | "enabled" | "disabled",
  tree: A11yNode[],
): { met: boolean; detail: string } {
  const target = resolveTarget(selector, tree);
  const found = target !== null;

  switch (assertion) {
    case "visible":
      return { met: found, detail: found ? "element visible ✓" : "element not found ✗" };
    case "notVisible":
      return { met: !found, detail: !found ? "element not visible ✓" : "element is visible ✗" };
    case "enabled": {
      if (!found) return { met: false, detail: "element not found ✗" };
      const flat = flatNodes(tree);
      const node = flat.find((n) => {
        const cx = (n.bounds.left + n.bounds.right) / 2;
        const cy = (n.bounds.top + n.bounds.bottom) / 2;
        return Math.abs(cx - target.x) < 5 && Math.abs(cy - target.y) < 5;
      });
      const enabled = node?.enabled !== false;
      return { met: enabled, detail: enabled ? "element enabled ✓" : "element disabled ✗" };
    }
    case "disabled": {
      if (!found) return { met: false, detail: "element not found ✗" };
      const flat2 = flatNodes(tree);
      const node2 = flat2.find((n) => {
        const cx = (n.bounds.left + n.bounds.right) / 2;
        const cy = (n.bounds.top + n.bounds.bottom) / 2;
        return Math.abs(cx - target.x) < 5 && Math.abs(cy - target.y) < 5;
      });
      const disabled = node2?.enabled === false;
      return { met: disabled, detail: disabled ? "element disabled ✓" : "element enabled ✗" };
    }
  }
}

/* ── Inner step list executor (recursive, no outer callbacks) ────────────── */

interface InnerCtx {
  adb: Adb;
  dumpTree: (a: Adb) => Promise<A11yNode[]>;
  injectTap: (a: Adb, x: number, y: number) => Promise<void>;
  stepDelayMs: number;
  ctrl: ExecutionController | undefined;
  vars: Record<string, unknown>;
  sharedFlowLoader?: ReplayerDeps["sharedFlowLoader"];
}

/**
 * Execute a list of steps recursively. Used for nested steps inside if/repeat/forEachRow.
 * Does NOT fire outer onStepStart/onStepResult callbacks.
 * Returns { results, allOk } — allOk is false if any step is not_found or error.
 */
async function executeInner(
  steps: IRStep[],
  ctx: InnerCtx,
): Promise<{ results: ReplayStepResult[]; allOk: boolean }> {
  const results: ReplayStepResult[] = [];
  let allOk = true;

  for (let i = 0; i < steps.length; i++) {
    if (ctx.ctrl?.stopped) break;
    if (ctx.ctrl?.paused) {
      const stopped = await waitWhilePaused(ctx.ctrl);
      if (stopped) break;
    }

    const step = steps[i]!;
    try {
      const r = await dispatchStep(step, i, ctx);
      results.push(r);
      if (r.status !== "ok") { allOk = false; break; }
    } catch (err) {
      // Propagate control-flow signals up — don't absorb them here
      if (err instanceof BreakLoopSignal || err instanceof ContinueLoopSignal || err instanceof StopTestSignal) throw err;
      const msg = err instanceof Error ? err.message : String(err);
      results.push({ stepIndex: i, stepId: step.id, status: "error", detail: msg });
      allOk = false;
      break;
    }
  }

  return { results, allOk };
}

/** Single-step dispatch shared between the outer loop and executeInner. */
async function dispatchStep(step: IRStep, i: number, ctx: InnerCtx): Promise<ReplayStepResult> {
  const { adb, dumpTree, injectTap, stepDelayMs, ctrl, vars } = ctx;

  // ── tap ──────────────────────────────────────────────────────────────────
  if (step.type === "tap") {
    const tree = await dumpTree(adb);
    const target = resolveWithFallback(step, tree);
    if (target) {
      await injectTap(adb, target.x, target.y);
      if (stepDelayMs > 0) await sleep(stepDelayMs);
      return { stepIndex: i, stepId: step.id, status: "ok", x: target.x, y: target.y, matchedBy: target.matchedBy };
    }
    const healHint = step.selector?.fingerprint
      ? "Fingerprint recorded — export test case and run via CLI with --heal command for healing"
      : undefined;
    return { stepIndex: i, stepId: step.id, status: "not_found", healHint };

  // ── doubleTap ─────────────────────────────────────────────────────────────
  } else if (step.type === "doubleTap") {
    const tree2 = await dumpTree(adb);
    const target2 = resolveWithFallback(step, tree2);
    if (target2) {
      await sleep(100);
      await injectTap(adb, target2.x, target2.y);
      await sleep(80);
      await injectTap(adb, target2.x, target2.y);
      if (stepDelayMs > 0) await sleep(stepDelayMs);
      return { stepIndex: i, stepId: step.id, status: "ok", x: target2.x, y: target2.y, matchedBy: target2.matchedBy };
    }
    return { stepIndex: i, stepId: step.id, status: "not_found" };

  // ── longPress ─────────────────────────────────────────────────────────────
  } else if (step.type === "longPress") {
    const tree3 = await dumpTree(adb);
    const target3 = resolveWithFallback(step, tree3);
    if (target3) {
      const dur = step.durationMs ?? 800;
      await adb.subprocess.noneProtocol.spawnWaitText([
        "input", "swipe",
        target3.x.toString(), target3.y.toString(),
        target3.x.toString(), target3.y.toString(),
        dur.toString(),
      ]);
      if (stepDelayMs > 0) await sleep(stepDelayMs);
      return { stepIndex: i, stepId: step.id, status: "ok", x: target3.x, y: target3.y, matchedBy: target3.matchedBy, detail: `held ${dur}ms` };
    }
    return { stepIndex: i, stepId: step.id, status: "not_found" };

  // ── swipe ─────────────────────────────────────────────────────────────────
  } else if (step.type === "swipe") {
    const [x1, y1] = step.from;
    const [x2, y2] = step.to;
    const dur2 = step.durationMs ?? 300;
    await adb.subprocess.noneProtocol.spawnWaitText([
      "input", "swipe", x1.toString(), y1.toString(), x2.toString(), y2.toString(), dur2.toString(),
    ]);
    if (stepDelayMs > 0) await sleep(stepDelayMs);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `swipe (${x1},${y1})→(${x2},${y2}) ${dur2}ms` };

  // ── clearText ─────────────────────────────────────────────────────────────
  } else if (step.type === "clearText") {
    const tree4 = await dumpTree(adb);
    const target4 = resolveWithFallback(step, tree4);
    if (target4) {
      await injectTap(adb, target4.x, target4.y);
      await sleep(100);
      const backspaces = Array(200).fill("KEYCODE_DEL");
      await adb.subprocess.noneProtocol.spawnWaitText(["input", "keyevent", ...backspaces]);
      if (stepDelayMs > 0) await sleep(stepDelayMs);
      return { stepIndex: i, stepId: step.id, status: "ok", x: target4.x, y: target4.y, matchedBy: target4.matchedBy, detail: "cleared text" };
    }
    return { stepIndex: i, stepId: step.id, status: "not_found" };

  // ── hideKeyboard ──────────────────────────────────────────────────────────
  } else if (step.type === "hideKeyboard") {
    await adb.subprocess.noneProtocol.spawnWaitText(["input", "keyevent", "KEYCODE_BACK"]);
    if (stepDelayMs > 0) await sleep(stepDelayMs);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: "keyboard hidden" };

  // ── scrollUntilVisible ────────────────────────────────────────────────────
  } else if (step.type === "scrollUntilVisible") {
    const direction = step.direction ?? "down";
    const maxScrolls = step.maxScrolls ?? 10;
    let found5 = false;
    let matchedBy5 = "";
    for (let s = 0; s < maxScrolls; s++) {
      if (ctrl?.stopped) break;
      const tree5 = await dumpTree(adb);
      const target5 = resolveTarget(step.selector, tree5);
      if (target5) { found5 = true; matchedBy5 = target5.matchedBy; break; }
      const sw = 1080; const sh = 2340;
      const scrollMap: Record<string, [[number,number],[number,number]]> = {
        down:  [[sw*0.5,sh*0.7],[sw*0.5,sh*0.3]],
        up:    [[sw*0.5,sh*0.3],[sw*0.5,sh*0.7]],
        left:  [[sw*0.8,sh*0.5],[sw*0.2,sh*0.5]],
        right: [[sw*0.2,sh*0.5],[sw*0.8,sh*0.5]],
      };
      const [[fx,fy],[tx,ty]] = scrollMap[direction]!;
      await adb.subprocess.noneProtocol.spawnWaitText(["input","swipe",fx.toString(),fy.toString(),tx.toString(),ty.toString(),"300"]);
      await sleep(500);
    }
    if (found5) return { stepIndex: i, stepId: step.id, status: "ok", matchedBy: matchedBy5, detail: `scrolled ${direction} until visible` };
    return { stepIndex: i, stepId: step.id, status: "not_found", detail: `not found after ${maxScrolls} scrolls` };

  // ── waitFor ───────────────────────────────────────────────────────────────
  } else if (step.type === "waitFor") {
    const targetState = step.state ?? "visible";
    const timeoutMs = step.timeoutMs ?? 10000;
    const pollMs = 300;
    let elapsed2 = 0;
    let matched2 = false;
    let matchedBy6 = "";
    while (elapsed2 < timeoutMs) {
      if (ctrl?.stopped) break;
      const tree6 = await dumpTree(adb);
      const t6 = resolveTarget(step.selector, tree6);
      if (targetState === "visible" && t6) { matched2 = true; matchedBy6 = t6.matchedBy; break; }
      if (targetState === "hidden" && !t6) { matched2 = true; matchedBy6 = "absent"; break; }
      if ((targetState === "enabled" || targetState === "disabled") && t6) {
        const flat3 = flatNodes(tree6);
        const n3 = flat3.find((n) => Math.abs((n.bounds.left+n.bounds.right)/2-t6.x)<5 && Math.abs((n.bounds.top+n.bounds.bottom)/2-t6.y)<5);
        const isEnabled = n3?.enabled !== false;
        if (targetState === "enabled" && isEnabled) { matched2 = true; matchedBy6 = t6.matchedBy; break; }
        if (targetState === "disabled" && !isEnabled) { matched2 = true; matchedBy6 = t6.matchedBy; break; }
      }
      await sleep(pollMs);
      elapsed2 += pollMs;
    }
    if (matched2) return { stepIndex: i, stepId: step.id, status: "ok", matchedBy: matchedBy6, detail: `element is ${targetState}` };
    return { stepIndex: i, stepId: step.id, status: "not_found", detail: `timeout: element not ${targetState} after ${timeoutMs}ms` };

  // ── openUrl ───────────────────────────────────────────────────────────────
  } else if (step.type === "openUrl") {
    await adb.subprocess.noneProtocol.spawnWaitText(["am", "start", "-a", "android.intent.action.VIEW", "-d", step.url]);
    if (stepDelayMs > 0) await sleep(stepDelayMs);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `opened ${step.url}` };

  // ── deepLink ──────────────────────────────────────────────────────────────
  } else if (step.type === "deepLink") {
    const cmd = step.packageName
      ? ["am","start","-a","android.intent.action.VIEW","-d",step.url,"-p",step.packageName]
      : ["am","start","-a","android.intent.action.VIEW","-d",step.url];
    await adb.subprocess.noneProtocol.spawnWaitText(cmd);
    if (stepDelayMs > 0) await sleep(stepDelayMs);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `deep link ${step.url}` };

  // ── setDeviceState ────────────────────────────────────────────────────────
  } else if (step.type === "setDeviceState") {
    const details2: string[] = [];
    if (step.wifi !== undefined) {
      await adb.subprocess.noneProtocol.spawnWaitText(["svc","wifi",step.wifi?"enable":"disable"]);
      details2.push(`wifi=${step.wifi?"on":"off"}`);
    }
    if (step.airplane !== undefined) {
      await adb.subprocess.noneProtocol.spawnWaitText(["settings","put","global","airplane_mode_on",step.airplane?"1":"0"]);
      // The settings write alone is not enough — must broadcast the intent so the radio actually changes
      await adb.subprocess.noneProtocol.spawnWaitText(["am","broadcast","-a","android.intent.action.AIRPLANE_MODE","--ez","state",step.airplane?"true":"false"]);
      details2.push(`airplane=${step.airplane?"on":"off"}`);
    }
    if (step.darkMode !== undefined) {
      await adb.subprocess.noneProtocol.spawnWaitText(["cmd","uimode","night",step.darkMode?"yes":"no"]);
      details2.push(`darkMode=${step.darkMode?"on":"off"}`);
    }
    if (step.location !== undefined) {
      // 3 = high-accuracy (GPS+network), 0 = off
      await adb.subprocess.noneProtocol.spawnWaitText(["settings","put","secure","location_mode",step.location?"3":"0"]);
      details2.push(`location=${step.location?"on":"off"}`);
    }
    if (step.showKeyboard) {
      // KEYCODE_SHOW_IME (221) — shows the soft keyboard on most Android versions
      await adb.subprocess.noneProtocol.spawnWaitText(["input","keyevent","221"]);
      details2.push("showKeyboard");
    }
    if (stepDelayMs > 0) await sleep(stepDelayMs);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: details2.join(", ") };

  // ── launchApp ─────────────────────────────────────────────────────────────
  } else if (step.type === "launchApp") {
    if (step.clearState) await adb.subprocess.noneProtocol.spawnWaitText(["pm","clear",step.packageName]);
    await adb.subprocess.noneProtocol.spawnWaitText(["monkey","-p",step.packageName,"-c","android.intent.category.LAUNCHER","1"]);
    if (stepDelayMs > 0) await sleep(stepDelayMs);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `opened ${step.packageName}` };

  // ── stopApp ───────────────────────────────────────────────────────────────
  } else if (step.type === "stopApp") {
    await adb.subprocess.noneProtocol.spawnWaitText(["am","force-stop",step.packageName]);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `stopped ${step.packageName}` };

  // ── pressKey ──────────────────────────────────────────────────────────────
  } else if (step.type === "pressKey") {
    await adb.subprocess.noneProtocol.spawnWaitText(["input","keyevent",KEYCODE_MAP[step.key]]);
    if (stepDelayMs > 0) await sleep(stepDelayMs);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: step.key };

  // ── wait ──────────────────────────────────────────────────────────────────
  } else if (step.type === "wait") {
    const tick = 100;
    let elapsed3 = 0;
    while (elapsed3 < step.durationMs) {
      if (ctrl?.stopped) break;
      if (ctrl?.paused) { await waitWhilePaused(ctrl); }
      await sleep(Math.min(tick, step.durationMs - elapsed3));
      elapsed3 += tick;
    }
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `waited ${step.durationMs}ms` };

  // ── screenshot ────────────────────────────────────────────────────────────
  } else if (step.type === "screenshot") {
    // screencap -p → stdout PNG → base64 text → decoded bytes → OPFS
    // (screenshotRef is filled by replaySteps() after this returns)
    return { stepIndex: i, stepId: step.id, status: "ok", detail: step.label ?? "screenshot" };

  // ── typeText ──────────────────────────────────────────────────────────────
  } else if (step.type === "typeText") {
    const escaped2 = step.text.replace(/ /g, "%s");
    await adb.subprocess.noneProtocol.spawnWaitText(["input","text",escaped2]);
    if (stepDelayMs > 0) await sleep(stepDelayMs);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: step.sensitive ? `typed "••••••••" (masked)` : `typed "${step.text}"` };

  // ── print ─────────────────────────────────────────────────────────────────
  } else if (step.type === "print") {
    const resolved2 = interpolate(step.message, vars);
    return { stepIndex: i, stepId: step.id, status: "ok", detail: resolved2, matchedBy: `[${step.level ?? "info"}]` };

  // ── setVariable ───────────────────────────────────────────────────────────
  // Writes to the mutable ctx.vars so all subsequent steps in this run see it.
  } else if (step.type === "setVariable") {
    let captured: string;
    if (step.source.kind === "literal") {
      captured = String(step.source.value);
    } else if (step.source.kind === "random") {
      const { randomType, length = 8, min = 0, max = 999999 } = step.source;
      if (randomType === "number") {
        captured = String(Math.floor(Math.random() * (max - min + 1)) + min);
      } else {
        const chars = randomType === "alphanumeric"
          ? "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
          : "abcdefghijklmnopqrstuvwxyz";
        captured = Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
      }
    } else if (step.source.kind === "attribute") {
      // attribute: dump tree, find element, read a specific attribute field
      const attrTree = await dumpTree(adb);
      const attrTarget = resolveTarget(step.source.selector, attrTree);
      if (!attrTarget) {
        return { stepIndex: i, stepId: step.id, status: "not_found", detail: `getAttribute: "${step.source.selector.description}" not found` };
      }
      const attrNode = flatNodes(attrTree).find((n) =>
        Math.abs((n.bounds.left + n.bounds.right) / 2 - attrTarget.x) < 5 &&
        Math.abs((n.bounds.top + n.bounds.bottom) / 2 - attrTarget.y) < 5,
      );
      if (!attrNode) {
        return { stepIndex: i, stepId: step.id, status: "not_found", detail: `getAttribute: node not found at resolved coords` };
      }
      const attr = step.source.attribute;
      let attrValue: string;
      switch (attr) {
        case "text":            attrValue = attrNode.text ?? ""; break;
        case "resourceId":      attrValue = attrNode.resourceId ?? ""; break;
        case "contentDesc":     attrValue = attrNode.contentDesc ?? ""; break;
        case "className":       attrValue = attrNode.className ?? ""; break;
        case "pkg":             attrValue = attrNode.pkg ?? ""; break;
        case "enabled":         attrValue = String(attrNode.enabled); break;
        case "checked":         attrValue = String(attrNode.checked); break;
        case "clickable":       attrValue = String(attrNode.clickable); break;
        case "focused":         attrValue = String(attrNode.focused); break;
        case "selected":        attrValue = String(attrNode.selected); break;
        case "scrollable":      attrValue = String(attrNode.scrollable); break;
        case "longClickable":   attrValue = String(attrNode.longClickable); break;
        case "password":        attrValue = String(attrNode.password); break;
        case "checkable":       attrValue = String(attrNode.checkable); break;
        case "boundsLeft":      attrValue = String(attrNode.bounds.left); break;
        case "boundsTop":       attrValue = String(attrNode.bounds.top); break;
        case "boundsRight":     attrValue = String(attrNode.bounds.right); break;
        case "boundsBottom":    attrValue = String(attrNode.bounds.bottom); break;
        case "boundsWidth":     attrValue = String(attrNode.bounds.right - attrNode.bounds.left); break;
        case "boundsHeight":    attrValue = String(attrNode.bounds.bottom - attrNode.bounds.top); break;
        default:                attrValue = ""; break;
      }
      captured = attrValue;
    } else if (step.source.kind === "count") {
      // count: dump tree, count all elements matching any candidate
      const cntTree = await dumpTree(adb);
      const flat = flatNodes(cntTree);
      let matchCount = 0;
      for (const cand of step.source.selector.candidates) {
        const n = flat.filter((node) => {
          if (cand.strategy === "text")         return node.text === cand.value;
          if (cand.strategy === "textContains") return (node.text ?? "").includes(cand.value);
          if (cand.strategy === "contentDesc")  return node.contentDesc === cand.value;
          if (cand.strategy === "a11yId")       return node.resourceId === cand.value;
          if (cand.strategy === "className")    return node.className === cand.value;
          return false;
        }).length;
        if (n > matchCount) matchCount = n; // use the candidate that yields the most matches
      }
      captured = String(matchCount);
    } else {
      // capture: dump tree, find element, read its text or contentDesc
      const capTree = await dumpTree(adb);
      const capTarget = resolveTarget(step.source.selector, capTree);
      if (!capTarget) {
        return { stepIndex: i, stepId: step.id, status: "not_found", detail: `setVariable: element not found for "${step.source.selector.description}"` };
      }
      const capNode = flatNodes(capTree).find((n) =>
        Math.abs((n.bounds.left + n.bounds.right) / 2 - capTarget.x) < 5 &&
        Math.abs((n.bounds.top + n.bounds.bottom) / 2 - capTarget.y) < 5,
      );
      captured = capNode?.text ?? capNode?.contentDesc ?? "";
    }
    // Write into the shared vars object — visible to all subsequent dispatchStep calls
    vars[step.name] = captured;
    const srcKind = step.source.kind === "attribute" ? `attribute:${step.source.attribute}` : step.source.kind === "count" ? `count(${step.source.selector.description})` : step.source.kind;
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `${step.name} = "${captured}"`, matchedBy: srcKind };

  // ── evaluate ──────────────────────────────────────────────────────────────
  } else if (step.type === "evaluate") {
    const lhs = interpolate(step.left, vars);
    const rhs = step.right ? interpolate(step.right, vars) : undefined;
    const { met, detail: evalDetail } = evaluateComparison(lhs, step.operator, rhs, step.options ?? undefined);
    if (step.storeAs) vars[step.storeAs] = met ? "true" : "false";
    if (step.failIfFalse && !met) {
      return { stepIndex: i, stepId: step.id, status: "error", detail: `Condition false: ${evalDetail}` };
    }
    return { stepIndex: i, stepId: step.id, status: "ok", detail: evalDetail };

  // ── if ────────────────────────────────────────────────────────────────────
  } else if (step.type === "if") {
    let met: boolean; let condDetail: string;
    if (step.condition.kind === "value") {
      const lhs = interpolate(step.condition.left, vars);
      const rhs = step.condition.right ? interpolate(step.condition.right, vars) : undefined;
      ({ met, detail: condDetail } = evaluateComparison(lhs, step.condition.operator, rhs, step.condition.options ?? undefined));
    } else {
      const condTree = await dumpTree(adb);
      ({ met, detail: condDetail } = checkCondition(step.condition.selector, step.condition.assertion, condTree));
    }
    const branch = met ? step.then : (step.else ?? []);
    if (branch.length === 0) {
      return { stepIndex: i, stepId: step.id, status: "ok", detail: `if [${step.condition.assertion}] → ${condDetail} → no ${met ? "then" : "else"} steps` };
    }
    const { results: innerResults, allOk: innerOk } = await executeInner(branch as IRStep[], ctx);
    const ok3 = innerResults.filter(r => r.status === "ok").length;
    const total3 = innerResults.length;
    return {
      stepIndex: i, stepId: step.id,
      status: innerOk ? "ok" : "error",
      detail: `if [${step.condition.assertion}] → ${condDetail} → ran ${met?"then":"else"} (${ok3}/${total3} ok)`,
    };

  // ── repeat ────────────────────────────────────────────────────────────────
  } else if (step.type === "repeat") {
    let completedIterations = 0;
    for (let iter = 0; iter < step.count; iter++) {
      if (ctrl?.stopped) break;
      if (ctrl?.paused) { const stopped2 = await waitWhilePaused(ctrl); if (stopped2) break; }
      try {
        const { allOk: iterOk } = await executeInner(step.steps as IRStep[], ctx);
        completedIterations++;
        if (!iterOk) {
          return { stepIndex: i, stepId: step.id, status: "error", detail: `repeat: iteration ${iter+1}/${step.count} failed` };
        }
      } catch (e) {
        if (e instanceof BreakLoopSignal) break;
        if (e instanceof ContinueLoopSignal) { completedIterations++; continue; }
        throw e;
      }
    }
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `repeat ×${completedIterations}/${step.count} complete` };

  // ── forEachRow ────────────────────────────────────────────────────────────
  } else if (step.type === "forEachRow") {
    // dataSource can be a JSON array literal for Phase 3.5 inline data
    let rows: Record<string, unknown>[] = [];
    try {
      const parsed = JSON.parse(step.dataSource) as unknown;
      if (Array.isArray(parsed)) rows = parsed as Record<string, unknown>[];
    } catch {
      return { stepIndex: i, stepId: step.id, status: "error", detail: `forEachRow: dataSource "${step.dataSource}" is not a JSON array. Use inline JSON like [{"key":"value"}]` };
    }
    if (rows.length === 0) {
      return { stepIndex: i, stepId: step.id, status: "ok", detail: `forEachRow: 0 rows — nothing to run` };
    }
    for (let r = 0; r < rows.length; r++) {
      if (ctrl?.stopped) break;
      const rowVars: Record<string, unknown> = { ...vars };
      // Bind ${variable.field} and ${variable} (direct row object as string)
      const row = rows[r]!;
      rowVars[step.variable] = JSON.stringify(row);
      for (const [k, v] of Object.entries(row)) {
        rowVars[`${step.variable}.${k}`] = v;
      }
      const rowCtx: InnerCtx = { ...ctx, vars: rowVars };
      try {
        const { allOk: rowOk } = await executeInner(step.steps as IRStep[], rowCtx);
        if (!rowOk) {
          return { stepIndex: i, stepId: step.id, status: "error", detail: `forEachRow: row ${r+1}/${rows.length} failed` };
        }
      } catch (e) {
        if (e instanceof BreakLoopSignal) break;
        if (e instanceof ContinueLoopSignal) continue;
        throw e;
      }
    }
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `forEachRow: ${rows.length} row${rows.length!==1?"s":""} complete` };

  // ── checkpoint ────────────────────────────────────────────────────────────
  } else if (step.type === "checkpoint") {
    if (step.status === "fail") {
      return { stepIndex: i, stepId: step.id, status: "error", detail: `✗ FAIL: ${step.message}`, matchedBy: "CHECKPOINT" };
    }
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `✓ PASS: ${step.message}`, matchedBy: "CHECKPOINT" };

  // ── stopTest ──────────────────────────────────────────────────────────────
  } else if (step.type === "stopTest") {
    throw new StopTestSignal(step.status ?? "failed", step.message ?? "Test stopped");

  // ── break ─────────────────────────────────────────────────────────────────
  } else if (step.type === "break") {
    throw new BreakLoopSignal();

  // ── continue ──────────────────────────────────────────────────────────────
  } else if (step.type === "continue") {
    throw new ContinueLoopSignal();

  // ── assert ────────────────────────────────────────────────────────────────
  } else if (step.type === "assert") {
    const assertTree = await dumpTree(adb);
    const assertTarget = resolveTarget(step.selector, assertTree);
    const found = assertTarget !== null;

    // For attribute checks (enabled/disabled/checked/unchecked/hasText),
    // find the actual tree node at the resolved coordinates.
    function getNode() {
      if (!assertTarget) return undefined;
      return flatNodes(assertTree).find((n) =>
        Math.abs((n.bounds.left + n.bounds.right) / 2 - assertTarget.x) < 5 &&
        Math.abs((n.bounds.top + n.bounds.bottom) / 2 - assertTarget.y) < 5,
      );
    }

    let passed = false;
    let detail = "";

    switch (step.assertion) {
      case "visible":
        passed = found;
        detail = found
          ? `✓ "${step.selector.description}" is visible`
          : `✗ "${step.selector.description}" not found on screen`;
        break;
      case "notVisible":
        passed = !found;
        detail = !found
          ? `✓ "${step.selector.description}" is not visible`
          : `✗ "${step.selector.description}" is visible but expected hidden`;
        break;
      case "enabled": {
        const n = getNode();
        passed = found && n?.enabled !== false;
        detail = passed
          ? `✓ "${step.selector.description}" is enabled`
          : found ? `✗ "${step.selector.description}" is disabled` : `✗ "${step.selector.description}" not found`;
        break;
      }
      case "disabled": {
        const n2 = getNode();
        passed = found && n2?.enabled === false;
        detail = passed
          ? `✓ "${step.selector.description}" is disabled`
          : found ? `✗ "${step.selector.description}" is enabled` : `✗ "${step.selector.description}" not found`;
        break;
      }
      case "checked": {
        const n3 = getNode();
        passed = found && n3?.checked === true;
        detail = passed
          ? `✓ "${step.selector.description}" is checked`
          : found ? `✗ "${step.selector.description}" is unchecked` : `✗ "${step.selector.description}" not found`;
        break;
      }
      case "unchecked": {
        const n4 = getNode();
        passed = found && n4?.checked !== true;
        detail = passed
          ? `✓ "${step.selector.description}" is unchecked`
          : found ? `✗ "${step.selector.description}" is checked` : `✗ "${step.selector.description}" not found`;
        break;
      }
      case "hasText": {
        const n5 = getNode();
        const actualText = n5?.text ?? n5?.contentDesc ?? "";
        const expected = step.value ?? "";
        passed = found && actualText === expected;
        detail = passed
          ? `✓ "${step.selector.description}" has text "${expected}"`
          : found
            ? `✗ expected text "${expected}" but got "${actualText}"`
            : `✗ "${step.selector.description}" not found`;
        break;
      }
    }

    return {
      stepIndex: i,
      stepId: step.id,
      status: passed ? "ok" : "error",
      detail,
      matchedBy: assertTarget?.matchedBy,
    };

  // ── api ───────────────────────────────────────────────────────────────────
  } else if (step.type === "api") {
    const url = interpolate(step.url, vars);
    const fetchOpts: RequestInit = { method: step.method };
    if (step.headers) fetchOpts.headers = step.headers;
    if (step.body !== undefined) {
      fetchOpts.body = typeof step.body === "string" ? step.body : JSON.stringify(step.body);
      const h = (fetchOpts.headers ?? {}) as Record<string, string>;
      if (!h["Content-Type"] && !h["content-type"]) h["Content-Type"] = "application/json";
      fetchOpts.headers = h;
    }
    let resp: Response;
    try { resp = await fetch(url, fetchOpts); }
    catch (fetchErr) {
      return { stepIndex: i, stepId: step.id, status: "error", detail: `API fetch failed: ${String(fetchErr)}` };
    }
    if (step.assertStatus && resp.status !== step.assertStatus) {
      return { stepIndex: i, stepId: step.id, status: "error", detail: `HTTP ${resp.status} ≠ expected ${step.assertStatus} for ${step.method} ${url}` };
    }
    const ct = resp.headers.get("content-type") ?? "";
    let body: unknown;
    if (ct.includes("json")) {
      body = await resp.json() as unknown;
      if (step.storeResponseAs) vars[step.storeResponseAs] = body;
    } else {
      body = await resp.text();
      if (step.storeResponseAs) vars[step.storeResponseAs] = body;
    }
    const bodyStr = typeof body === "string" ? body : JSON.stringify(body);
    return {
      stepIndex: i, stepId: step.id, status: "ok",
      detail: `${step.method} ${url} → HTTP ${resp.status} (${bodyStr.length} chars)${step.storeResponseAs ? ` → ${step.storeResponseAs}` : ""}`,
    };

  // ── holdSwipe ─────────────────────────────────────────────────────────────
  } else if (step.type === "holdSwipe") {
    const hsTree = await dumpTree(adb);
    const hsTarget = resolveTarget(step.selector, hsTree);
    if (!hsTarget) {
      return { stepIndex: i, stepId: step.id, status: "not_found", detail: `holdSwipe: "${step.selector.description}" not found` };
    }
    const { x: hsx, y: hsy, matchedBy: hsMb } = hsTarget;
    const dir = step.direction ?? "down";
    const dist = step.distance ?? 400;
    const endX = dir === "left" ? hsx - dist : dir === "right" ? hsx + dist : hsx;
    const endY = dir === "up" ? hsy - dist : dir === "down" ? hsy + dist : hsy;
    // Total duration = holdMs (stationary press) + 300ms swipe motion
    const totalMs = (step.holdMs ?? 800) + 300;
    await adb.subprocess.noneProtocol.spawnWaitText([
      "input", "touchscreen", "swipe",
      String(hsx), String(hsy), String(endX), String(endY), String(totalMs),
    ]);
    if (stepDelayMs > 0) await sleep(stepDelayMs);
    return {
      stepIndex: i, stepId: step.id, status: "ok", x: hsx, y: hsy,
      detail: `hold ${step.holdMs ?? 800}ms + swipe ${dir} ${dist}px on "${step.selector.description}"`,
      matchedBy: hsMb,
    };

  // ── callFlow ─────────────────────────────────────────────────────────────
  } else if (step.type === "callFlow") {
    if (step.sharedFlowId) {
      // Shared Function Library call
      const fn = await ctx.sharedFlowLoader?.(step.sharedFlowId);
      if (!fn) {
        return { stepIndex: i, stepId: step.id, status: "error", detail: `Function "${step.sharedFlowName ?? step.sharedFlowId}" not found in library` };
      }
      // Build child vars: start with parent vars, add bound args
      const childVars: Record<string, unknown> = { ...vars };
      for (const param of fn.parameters) {
        const rawArg = step.args?.[param.name] ?? param.defaultValue ?? "";
        childVars[param.name] = interpolate(String(rawArg), vars);
      }
      const childCtx: InnerCtx = { ...ctx, vars: childVars };
      const { results: innerResults, allOk } = await executeInner(fn.steps, childCtx);
      // Merge child vars back so function can set variables for caller
      Object.assign(vars, childVars);
      const ok3 = innerResults.filter(r => r.status === "ok").length;
      return {
        stepIndex: i, stepId: step.id,
        status: allOk ? "ok" : "error",
        detail: `callFlow "${fn.name}" — ${ok3}/${innerResults.length} steps OK`,
      };
    }
    // In-test flow call (flowId within same TestIR — not yet implemented for Studio)
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `callFlow ${step.flowId ?? "(no flowId)"}: in-test flows not yet supported in Studio` };

  } else {
    return { stepIndex: i, stepId: step.id, status: "ok", detail: `${(step as {type:string}).type}: not yet implemented` };
  }
}

// ── script step helper — called from replaySteps after dispatchStep ───────────

export async function runScriptStep(
  code: string,
  vars: Record<string, unknown>,
  scriptRunner: ((code: string, vars: Record<string, unknown>) => Promise<ScriptResult>) | undefined,
): Promise<{ ok: boolean; detail: string; updatedVars?: Record<string, unknown> }> {
  if (!scriptRunner) {
    return { ok: false, detail: "Script sandbox not available — connect a device and ensure the Studio iframe is ready." };
  }
  try {
    const result = await scriptRunner(code, vars);
    if (result.fail) return { ok: false, detail: `Script: ${result.fail}` };
    return { ok: true, detail: "Script executed", updatedVars: result.vars };
  } catch (err) {
    return { ok: false, detail: `Script error: ${err instanceof Error ? err.message : String(err)}` };
  }
}

/* ── OPFS screenshot helper ──────────────────────────────────────────────────
 * Pulls a PNG screenshot from the device via base64 pipe and writes it to OPFS.
 * Returns the OPFS path on success, undefined on any error (non-fatal).
 */
async function captureScreenshotToOpfs(
  adb: Adb,
  runId: string,
  stepIndex: number,
): Promise<string | undefined> {
  const tmpFile = `/sdcard/mimiq_ss_${stepIndex}.png`;
  try {
    // Write PNG to a temp file first (piping binary through stdout corrupts data)
    await adb.subprocess.noneProtocol.spawnWaitText(["screencap", "-p", tmpFile]);
    // Read and base64-encode the saved PNG file
    const b64raw = await adb.subprocess.noneProtocol.spawnWaitText(["base64", tmpFile]);
    // Clean up the temp file (fire-and-forget)
    adb.subprocess.noneProtocol.spawnWaitText(["rm", "-f", tmpFile]).catch(() => {});
    const b64 = b64raw.replace(/\s/g, "");
    if (!b64) return undefined;
    // Verify PNG signature (first bytes should be \x89PNG)
    const sample = atob(b64.slice(0, 8));
    if (sample.charCodeAt(0) !== 0x89 || sample.charCodeAt(1) !== 0x50) return undefined;
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j);
    const root = await navigator.storage.getDirectory();
    const runsDir = await root.getDirectoryHandle("runs", { create: true });
    const runDir = await runsDir.getDirectoryHandle(runId, { create: true });
    const fh = await runDir.getFileHandle(`step_${stepIndex}.png`, { create: true });
    const writable = await fh.createWritable();
    await writable.write(bytes);
    await writable.close();
    return `runs/${runId}/step_${stepIndex}.png`;
  } catch {
    adb.subprocess.noneProtocol.spawnWaitText(["rm", "-f", tmpFile]).catch(() => {});
    return undefined;
  }
}

/* ── Public API ──────────────────────────────────────────────────────────────*/

export async function replaySteps(
  steps: IRStep[],
  adb: Adb,
  deps: ReplayerDeps = {},
): Promise<ReplayStepResult[]> {
  const dumpTreeFn =
    deps.dumpTree ??
    ((a: Adb) => import("./treeParser.js").then((m) => m.dumpTree(a)));
  const injectTap = deps.injectTap ?? defaultInjectTap;
  const stepDelayMs = deps.stepDelayMs ?? DEFAULT_STEP_DELAY_MS;
  const ctrl = deps.controller;
  const ctx: InnerCtx = { adb, dumpTree: dumpTreeFn, injectTap, stepDelayMs, ctrl, vars: deps.runContext ?? {}, sharedFlowLoader: deps.sharedFlowLoader };

  const results: ReplayStepResult[] = [];

  for (let i = 0; i < steps.length; i++) {
    if (ctrl) {
      if (ctrl.stopped) break;
      if (ctrl.paused) {
        const stopped = await waitWhilePaused(ctrl);
        if (stopped) break;
      }
    }

    const step = steps[i]!;
    deps.onStepStart?.(i);

    try {
      // ── script steps are handled here (need scriptRunner + mutable vars) ──
      let r: ReplayStepResult;
      if (step.type === "script") {
        const { ok, detail, updatedVars } = await runScriptStep(step.code, ctx.vars, deps.scriptRunner);
        if (updatedVars) Object.assign(ctx.vars, updatedVars);
        r = { stepIndex: i, stepId: step.id, status: ok ? "ok" : "error", detail };
      } else {
        r = await dispatchStep(step, i, ctx);
      }

      // Pull screenshot to OPFS when explicitly requested or "capture every step" is on
      const shouldCapture = deps.runId && (step.type === "screenshot" || deps.captureEveryStep);
      if (shouldCapture && r.status !== "error") {
        const ref = await captureScreenshotToOpfs(adb, deps.runId!, i);
        if (ref) r = { ...r, screenshotRef: ref };
      }

      // Capture a11y tree dump on failure for debugging (when setting enabled)
      if (r.status !== "ok" && deps.captureLogsOnFailure) {
        try {
          const failTree = await dumpTreeFn(adb);
          r = { ...r, a11yDump: JSON.stringify(failTree) };
        } catch { /* non-fatal — logs best-effort */ }
      }

      results.push(r);
      deps.onStepResult?.(r);
      if (ctrl?.stopped) break;
    } catch (err) {
      if (err instanceof StopTestSignal) {
        const r: ReplayStepResult = {
          stepIndex: i, stepId: step.id,
          status: err.verdict === "passed" ? "ok" : "error",
          detail: `Test stopped: ${err.message}`,
          matchedBy: err.verdict === "passed" ? "[STOPPED-PASS]" : "[STOPPED-FAIL]",
        };
        results.push(r);
        deps.onStepResult?.(r);
        break; // halt — no more steps
      }
      if (err instanceof BreakLoopSignal || err instanceof ContinueLoopSignal) {
        const r: ReplayStepResult = { stepIndex: i, stepId: step.id, status: "error", detail: `${err.message} used outside a loop` };
        results.push(r);
        deps.onStepResult?.(r);
        continue;
      }
      const msg = err instanceof Error ? err.message : String(err);
      const r: ReplayStepResult = { stepIndex: i, stepId: step.id, status: "error", detail: msg };
      results.push(r);
      deps.onStepResult?.(r);
    }
  }

  return results;
}
